import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useLogin, useLoginTotp, useSession } from '../../hooks/useSession';
import { useFeedback } from '../../components/Feedback/FeedbackProvider';
import { ApiRequestError } from '../../lib/api';
import { EyeToggle } from './EyeToggle';
import s from './Auth.module.css';

/**
 * Tela de acesso ao painel.
 *
 * A coluna da esquerda e uma foto fixa do escritorio sob filtro azul, com as
 * citacoes institucionais girando por cima.
 *
 * A foto era um rodizio de quatro, com os keyframes das fotos amarrados aos das
 * frases para as duas trocarem no mesmo instante. Com uma imagem so, nao ha o
 * que cruzar: a foto fica parada e as citacoes seguem sozinhas, uma a cada 8s.
 */

const QUOTES = [
  'Construindo e protegendo patrimônio para gerações',
  'Cuidando de histórias, legados e sonhos',
  'Seu patrimônio merece mais do que gestão. Merece cuidado',
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  /** Preenchido quando a senha passa e a conta exige o codigo. */
  const [pedindoCodigo, setPedindoCodigo] = useState(false);
  const [codigo, setCodigo] = useState('');

  const login = useLogin();
  const loginTotp = useLoginTotp();
  const feedback = useFeedback();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isLoading } = useSession();

  // Quem ja tem sessao valida nao ve o formulario.
  if (!isLoading && session) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  function entrar(nome: string) {
    feedback.success(`Bem-vindo, ${nome.split(' ')[0] ?? ''}.`);
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    void navigate(from, { replace: true });
  }

  async function enviarCodigo(event: FormEvent) {
    event.preventDefault();
    if (loginTotp.isPending) return;

    try {
      const result = await loginTotp.mutateAsync(codigo);
      entrar(result.user.name);
    } catch (error) {
      setCodigo('');
      if (error instanceof ApiRequestError) {
        // 401 aqui e desafio expirado: manda de volta para a senha, senao a
        // pessoa fica digitando codigo numa tela que nao aceita mais nenhum.
        if (error.status === 401 && error.message.includes('expirada')) {
          setPedindoCodigo(false);
          setPassword('');
        }
        feedback.error(
          error.status === 429 ? 'Acesso bloqueado temporariamente' : 'Codigo nao aceito',
          error.message,
        );
      } else {
        feedback.error('Nao foi possivel entrar', 'Tente novamente em instantes.');
      }
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (login.isPending) return;

    try {
      const result = await login.mutateAsync({ email, password, remember });

      // Senha certa nao significa sessao: a conta pode exigir a segunda etapa.
      if ('precisa_totp' in result) {
        setPedindoCodigo(true);
        return;
      }

      entrar(result.user.name);
    } catch (error) {
      // Toda falha termina em card: nada de erro so no DevTools.
      if (error instanceof ApiRequestError) {
        feedback.error(
          error.status === 429 ? 'Acesso bloqueado temporariamente' : 'Nao foi possivel entrar',
          error.message,
        );
      } else {
        feedback.error('Nao foi possivel entrar', 'Tente novamente em instantes.');
      }
    }
  }

  return (
    <div className={s.loginScreen}>
      <div className={s.loginMedia}>
        <div
          className={s.loginPhoto}
          style={{ backgroundImage: "url('/assets/office-4.jpeg')" }}
        />
        <div className={s.loginTint} />
        <div className={s.loginFade} />
        <div className={s.loginGlow} />

        <div className={s.loginQuotes}>
          {QUOTES.map((quote, index) => (
            <p
              key={quote}
              className={`${s.loginQuote} ${[s.loginQuote1, s.loginQuote2, s.loginQuote3][index] ?? ''}`}
            >
              &ldquo;{quote}&rdquo;
            </p>
          ))}
        </div>
      </div>

      <div className={s.loginPanel}>
        <div className={s.loginCard}>
          <img className={s.loginLogo} src="/assets/apen-logo.png" alt="Apen Capital" />

          <div className={s.loginHeading}>
            <h1>{pedindoCodigo ? 'Verificação em duas etapas' : 'Acessar o painel'}</h1>
            <p>
              {pedindoCodigo
                ? 'Digite o código de 6 dígitos do seu aplicativo autenticador.'
                : 'Entre com suas credenciais para continuar.'}
            </p>
          </div>

          {/* O formulário da senha sai de cena inteiro. Deixá-lo visível junto
              do código sugeriria que dá para corrigir a senha aqui, e não dá:
              o desafio já foi emitido e é ele que vale agora. */}
          {pedindoCodigo ? (
            <form className={s.loginForm} onSubmit={(e) => void enviarCodigo(e)} noValidate>
              <div className={s.input}>
                <label htmlFor="codigo">Código</label>
                <input
                  id="codigo"
                  className={s.codigoTotp}
                  /* `one-time-code` é o que faz o iOS e o Android oferecerem o
                     código copiado do app no teclado. */
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  placeholder="000000"
                  maxLength={7}
                  autoFocus
                  value={codigo}
                  onChange={(e) => {
                    setCodigo(e.target.value);
                  }}
                  disabled={loginTotp.isPending}
                  required
                />
              </div>

              <p className={s.dicaCodigo}>
                O código está no seu aplicativo autenticador e muda a cada 30
                segundos. Se ele virar enquanto você digita, use o próximo.
              </p>

              <div className={s.loginActions}>
                <button
                  type="submit"
                  className={s.btnPrimary}
                  disabled={loginTotp.isPending || codigo.replace(/\s/g, '').length < 6}
                >
                  {loginTotp.isPending ? (
                    <span className={s.btnLoader}>
                      <span className={s.spinner} aria-hidden="true" />
                      Conferindo…
                    </span>
                  ) : (
                    'Confirmar'
                  )}
                </button>
              </div>

              <button
                type="button"
                className={s.btnVoltar}
                onClick={() => {
                  setPedindoCodigo(false);
                  setCodigo('');
                  setPassword('');
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Entrar com outra conta
              </button>
            </form>
          ) : (
          <form className={s.loginForm} onSubmit={(e) => void handleSubmit(e)} noValidate>
            <div className={s.input}>
              <label htmlFor="email">Usuário</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="colaborador@apencapital.com.br"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                disabled={login.isPending}
                required
              />
            </div>

            <div className={s.input}>
              <label htmlFor="password">Senha</label>
              <div className={s.inputWrap}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  disabled={login.isPending}
                  required
                />
                <EyeToggle
                  className={s.inputEye}
                  visible={showPassword}
                  onToggle={() => {
                    setShowPassword((v) => !v);
                  }}
                />
              </div>
            </div>

            <div className={s.loginRow}>
              <label className={s.loginRemember}>
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => {
                    setRemember(e.target.checked);
                  }}
                />
                <span>Manter conectado</span>
              </label>
              <Link className={s.loginLink} to="/esqueci-senha">
                Esqueci a senha
              </Link>
            </div>

            <div className={s.loginActions}>
              <button type="submit" className={s.btnPrimary} disabled={login.isPending}>
                {login.isPending ? (
                  <span className={s.btnLoader}>
                    <span className={s.spinner} aria-hidden="true" />
                    Entrando…
                  </span>
                ) : (
                  'Entrar'
                )}
              </button>
            </div>
          </form>
          )}

          <p className={s.loginFooter}>Apen Capital · Painel financeiro</p>
        </div>
      </div>
    </div>
  );
}
